/**
 * Coach- and athlete-facing copy for checkpoint coverage (viewpoint
 * observability Phase 1).
 *
 * SINGLE SOURCE of every string the coverage line, the withheld-checkpoint
 * rows and the degraded-checkpoint note render. Keeping them here (rather than
 * inline in JSX) means the copy gate in docs/COACH_FACING_COPY_STANDARD.md §4
 * has one place to review, and the components stay layout-only.
 *
 * Every string answers the standard's three questions, in order:
 *   1. WHAT happened / what to do
 *   2. WHY, in one sentence a coach would say to another coach
 *   3. HOW — a concrete instruction
 *
 * and uses no internal vocabulary (no landmark names, no statistics, no
 * pipeline words). Pinned by `frontend/src/test/coachCopyDenylist.ts`.
 */
import type {
  AbstainedCheckpoint,
  EvaluationCoverage,
  GoalAbstentionReason,
  GoalDegradeReason,
} from '@/types/api';

/** "one" for 1, the numeral otherwise — "the other one" reads better than "the other 1". */
function countWord(n: number): string {
  return n === 1 ? 'one' : String(n);
}

function isPlural(n: number): boolean {
  return n !== 1;
}

// ---------------------------------------------------------------------------
// Withheld checkpoint (one row per abstained checkpoint)
// ---------------------------------------------------------------------------

/**
 * The generic stand-in for a body part the payload did not name. Used when
 * `body_part` is absent — a named part is always better ("The left knee was
 * hidden…" beats "The body part this check watches was hidden…").
 */
const GENERIC_BODY_PART = 'body part this check watches';

/**
 * Turn whatever the payload gives us into something a coach reads.
 *
 * `body_part` is contractually a plain-English display string, but it crosses
 * a service boundary and a stray `left_knee` would be exactly the leak
 * docs/COACH_FACING_COPY_STANDARD.md §2 forbids — and the frontend cannot fix
 * the backend. Underscores are therefore humanised rather than trusted, and
 * an empty/whitespace value falls back to the generic phrase.
 */
function bodyPartPhrase(bodyPart: string | null | undefined): string {
  const cleaned = (bodyPart ?? '').replace(/_/g, ' ').trim();
  return cleaned.length > 0 ? cleaned : GENERIC_BODY_PART;
}

/**
 * "both heels", "hips to shoulders", "lead wrist and lead foot" read as
 * plural; "left knee" does not. Keeps the owner-approved sentence grammatical
 * without re-wording it: subject + verb + pronoun agree.
 */
function isPluralBodyPart(phrase: string): boolean {
  const lower = phrase.toLowerCase();
  if (lower.startsWith('both ') || lower.includes(' and ') || lower.includes(' to ')) return true;
  const last = lower.split(' ').pop() ?? '';
  return last.endsWith('s') && !last.endsWith('ss');
}

/** "The left knee was hidden … blocks it" / "Both heels were hidden … blocks them". */
function hiddenSentence(bodyPart: string | null | undefined): string {
  const phrase = bodyPartPhrase(bodyPart);
  const plural = phrase !== GENERIC_BODY_PART && isPluralBodyPart(phrase);
  const subject = phrase.toLowerCase().startsWith('both ')
    ? phrase.charAt(0).toUpperCase() + phrase.slice(1)
    : `The ${phrase}`;
  return (
    `${subject} ${plural ? 'were' : 'was'} hidden for most of the movement. ` +
    `Make sure nothing blocks ${plural ? 'them' : 'it'} and the whole body stays in frame.`
  );
}

/**
 * The WHY + HOW for one withheld checkpoint. The WHAT is the row's own
 * headline, `withheldCheckpointHeadline()`.
 *
 * Text is the owner-approved wording in docs/COACH_FACING_COPY_STANDARD.md §3
 * and the Phase 1 contract. One deliberate addition:
 * `missing_template_reference` is given a HOW the contract omits, so the
 * message answers all three questions (§1).
 *
 * The visibility reasons interpolate the checkpoint's `body_part` when the
 * payload names one, and fall back to a generic phrase when it does not.
 */
const WITHHELD_REASON_COPY: Record<
  GoalAbstentionReason,
  (bodyPart: string | null | undefined) => string
> = {
  not_applicable_view: () =>
    'This check measures a bend you can only see from the side, and this video ' +
    'was filmed from the front. Film from the side to include it.',
  insufficient_visibility: (bodyPart) => hiddenSentence(bodyPart),
  insufficient_frames: (bodyPart) => hiddenSentence(bodyPart),
  event_not_detected: () =>
    "We couldn't find the moment this check looks for in the video. Make sure " +
    'the full movement, start to finish, is in the clip.',
  missing_template_reference: () =>
    "Your coach's demo doesn't contain this check, so there was nothing to " +
    'compare against. Ask your coach to include it in the demo.',
  evaluation_error: () =>
    'Something went wrong measuring this check. Try again; if it keeps ' +
    'happening, tell your coach.',
  misconfigured: () =>
    "This check can't be measured the way it's set up. Open the template " +
    'editor and fix this check — or ask us to, if it was suggested for you.',
  low_boundary_confidence: () =>
    "We couldn't pin down exactly when this part of the movement started and " +
    'ended in the video, so timing it would be a guess. Film the whole movement ' +
    'in one steady shot, start to finish, and try again.',
};

/** WHAT: "'Lead knee over toe' wasn't scored." */
export function withheldCheckpointHeadline(label: string): string {
  return `‘${label}’ wasn't scored.`;
}

/**
 * WHY + HOW for a withheld checkpoint.
 *
 * `bodyPart` is the checkpoint's optional plain-English body-part name; it is
 * used only by the visibility reasons, and only when present.
 */
export function withheldCheckpointReason(
  reason: GoalAbstentionReason,
  bodyPart?: string | null,
): string {
  const build = WITHHELD_REASON_COPY[reason];
  if (!build) {
    // Unknown future reason: say what we know and what to do, never the key.
    return (
      "We couldn't measure this check from this video. Try filming again from " +
      "the same angle as your coach's demo."
    );
  }
  return build(bodyPart);
}

// ---------------------------------------------------------------------------
// Degraded checkpoint note
// ---------------------------------------------------------------------------

/**
 * A degraded checkpoint still has a score — it is just measured from an angle
 * that flattens the movement, so the number is a guide rather than a
 * precise reading. Same sentence for both degrade reasons: the coach's action
 * is identical and naming the reason would add internal vocabulary without
 * adding an action.
 */
export const DEGRADED_CHECKPOINT_NOTE =
  'This check is less precise from this angle; read it as a rough guide.';

export function degradedCheckpointNote(_reason?: GoalDegradeReason | null): string {
  return DEGRADED_CHECKPOINT_NOTE;
}

// ---------------------------------------------------------------------------
// Coverage line (report hero)
// ---------------------------------------------------------------------------

/**
 * The WHY + HOW of the coverage line depends on the DOMINANT reason among the
 * withheld checkpoints — the one that accounts for the most of them. Ties
 * resolve by the order below, which puts the reasons with the clearest coach
 * action first.
 */
const DOMINANT_REASON_PRIORITY: GoalAbstentionReason[] = [
  'not_applicable_view',
  'insufficient_visibility',
  'insufficient_frames',
  'event_not_detected',
  'missing_template_reference',
  'evaluation_error',
];

export function dominantAbstentionReason(
  abstained: readonly AbstainedCheckpoint[],
): GoalAbstentionReason | null {
  if (abstained.length === 0) return null;

  const counts = new Map<GoalAbstentionReason, number>();
  for (const item of abstained) {
    counts.set(item.reason, (counts.get(item.reason) ?? 0) + 1);
  }

  let best: GoalAbstentionReason | null = null;
  let bestCount = 0;
  for (const reason of DOMINANT_REASON_PRIORITY) {
    const count = counts.get(reason) ?? 0;
    if (count > bestCount) {
      best = reason;
      bestCount = count;
    }
  }
  // A reason outside the known set still has to produce a sentence.
  if (best === null) {
    const first = abstained[0]?.reason;
    return first ?? null;
  }
  return best;
}

export interface CoverageSentences {
  /** WHAT: "We could check 3 of your 5 checkpoints from this video." */
  what: string;
  /** WHY: why the rest were left out. */
  why: string;
  /** HOW: what to change next time. */
  how: string;
}

/**
 * Builds the three coverage sentences. Returns `null` when there is nothing to
 * say — every configured checkpoint was assessed, or the payload is missing /
 * malformed. Callers render nothing on null (the common path).
 */
export function buildCoverageSentences(
  coverage: EvaluationCoverage | null | undefined,
): CoverageSentences | null {
  if (!coverage) return null;

  const { configured, assessed } = coverage;
  if (typeof configured !== 'number' || typeof assessed !== 'number') return null;
  if (configured <= 0) return null;
  // Full coverage: the line is omitted entirely rather than reassuring with
  // "all 5 of 5" — a message with nothing to do is noise (§1).
  if (assessed >= configured) return null;

  const withheld = configured - assessed;
  const abstained = coverage.abstained ?? [];
  const reason = dominantAbstentionReason(abstained);

  const what = `We could check ${assessed} of your ${configured} checkpoints from this video.`;
  const other = `The other ${countWord(withheld)}`;
  const were = isPlural(withheld) ? 'were' : 'was';
  const them = isPlural(withheld) ? 'them' : 'it';

  let why: string;
  let how: string;

  switch (reason) {
    case 'not_applicable_view':
      why =
        `${other} ${isPlural(withheld) ? 'measure bends' : 'measures a bend'} you can ` +
        `only see from the side, and this video was filmed from the front.`;
      how = `Film from the side to include ${them}.`;
      break;
    case 'insufficient_visibility':
    case 'insufficient_frames':
      why = `${other} ${were} hidden for most of the movement, so we didn't score ${them} rather than guess.`;
      how = 'Re-film with nothing blocking the body and the whole body in frame.';
      break;
    case 'event_not_detected':
      why = `We couldn't find the moment ${other.toLowerCase()} ${isPlural(withheld) ? 'look' : 'looks'} for.`;
      how = 'Re-film with the full movement, start to finish, in the clip.';
      break;
    case 'missing_template_reference':
      why = `Your coach's demo doesn't contain ${other.toLowerCase()}, so there was nothing to compare against.`;
      how = `Ask your coach to include ${them} in the demo.`;
      break;
    case 'evaluation_error':
      why = `Something went wrong measuring ${other.toLowerCase()}.`;
      how = 'Try again; if it keeps happening, tell your coach.';
      break;
    default:
      why = `${other} couldn't be measured from this video, so we didn't score ${them} rather than guess.`;
      how = "Re-film from the same angle as your coach's demo, with the whole body in frame.";
      break;
  }

  return { what, why, how };
}

// ---------------------------------------------------------------------------
// Far-side note (coach, template)
// ---------------------------------------------------------------------------

/**
 * Owner-approved wording, docs/COACH_FACING_COPY_STANDARD.md §3. WHAT / WHY /
 * HOW in three sentences.
 */
export function farSideNoteText(label: string): string {
  return (
    `‘${label}’ is on the side away from the camera in your demo. ` +
    'Checks on the far side are less accurate than checks on the near side. ' +
    'If this checkpoint matters most, film the demo with that side toward the ' +
    'camera, or keep it and read its score as a rough guide.'
  );
}

// ---------------------------------------------------------------------------
// Refused coach rule (coach, template)
// ---------------------------------------------------------------------------

/**
 * A rule the coach wrote that could not be turned into a measurable check.
 * Until now the coach saw nothing at all.
 */
export function refusedRuleText(text: string): string {
  return (
    `We couldn't turn this rule into a check: ${text}. Try rephrasing it as ` +
    'one body part, one direction, one moment.'
  );
}

/**
 * The same refusal, with the REASON the backend recorded beside the coach's own
 * phrase (PR-8, audit items `RC-13` / `E2E-9`).
 *
 * `metadata.refused_rule_texts` is a flat `string[]` that mixed the coach's
 * verbatim phrase with the interpreter's generated refusal sentences, and kept
 * the reason nowhere the coach could see it — so the list above could say what
 * happened but never why. `refused_rule_details` carries both; readers prefer
 * it and fall back to `refusedRuleText` for a template finalized before PR-8
 * (no reshape, no backfill — plan T-11).
 *
 * WHAT: this rule is not a check. WHY: the backend's own sentence, verbatim.
 * HOW: the same concrete instruction as the flat version.
 */
export function refusedRuleDetailText(text: string, reason: string): string {
  const why = reason.trim();
  return (
    `We couldn't turn this rule into a check: ${text}. ` +
    (why ? `${why.endsWith('.') ? why : `${why}.`} ` : '') +
    'Try rephrasing it as one body part, one direction, one moment.'
  );
}
